import { Component, OnInit } from '@angular/core';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { SignupWizardService } from '../signup-wizard.service';
import { SignupChannel, SupportedChannels, UnsupportedChannels } from '../signup.interface';

@Component({
  standalone: false,
  selector: 'sbnb-signup-channels',
  templateUrl: './signup-channels.component.html',
  styleUrls: ['./signup-channels.component.scss'],
})
export class SignupChannelsComponent implements OnInit {
  channels = {} as {
    [SignupChannel.TripAdvisor]?: boolean;
    [SignupChannel.BookingDotCom]?: boolean;
    [SignupChannel.Vrbo]?: boolean;
    [SignupChannel.Airbnb]?: boolean;
    [SignupChannel.Agoda]?: boolean;
    [SignupChannel.Direct]?: boolean;
  };

  atLeastOneChannelEnabled = false;
  saving: boolean;

  selectedAgoda = false;
  selectedTripAdvisor = false;
  selectedDirect = false;
  selectedMultipleUnsupported = false;

  stage = 1;

  isAirbnbSignup = false;

  constructor(
    public wizard: SignupWizardService,
    private readonly segmentIoService: SegmentIoService
  ) {
    this.isAirbnbSignup = this.wizard.isAirbnbSignup();
  }

  ngOnInit() {
    if (!this.wizard.signupUser.token) {
      this.wizard.cancel();
    }

    this.hydrate();
  }

  private hydrate() {
    if (this.wizard.signupUser.platforms?.length > 0) {
      this.wizard.signupUser.platforms.forEach((platform) => {
        this.channels[platform] = true;
      });

      this.isAirbnbSignup ? (this.channels[SignupChannel.Airbnb] = true) : false;

      this.isAtLeastOneEnabled();
    }
  }

  next() {
    this.saving = true;

    const channelsArr = [];
    for (const [key, value] of Object.entries(this.channels)) {
      if (value) {
        channelsArr.push(key);
      }
    }

    this.selectedAgoda = channelsArr.indexOf(SignupChannel.Agoda) > -1 ? true : false;
    this.selectedTripAdvisor = channelsArr.indexOf(SignupChannel.TripAdvisor) > -1 ? true : false;

    this.selectedDirect = channelsArr.indexOf(SignupChannel.Direct) > -1 ? true : false;

    this.selectedMultipleUnsupported = this.selectedAgoda && this.selectedTripAdvisor ? true : false;

    const onlySelectedDirect = this.selectedDirect && channelsArr.length === 1;

    // If only selected direct, we want to send to Airbnb Connect flow, so force select Airbnb
    const platforms = onlySelectedDirect ? [...channelsArr, ...[SignupChannel.Airbnb]] : [...channelsArr];

    this.wizard.updatePlatforms(platforms).subscribe(
      (res) => {
        this.saving = false;

        // If we have unsupported channels, show the interim 'supported platforms' step
        if (this.hasSupportedChannel() && this.hasUnSupportedChannel()) {
          this.segmentIoService.track(SegmentEvent.SignupUnsupportedChannelsSelected, {});
          this.stage = 2;
        }
        // Only selected direct
        else if (onlySelectedDirect) {
          this.segmentIoService.track(SegmentEvent.SignupUnsupportedChannelsSelected, {});
          this.stage = 2;
        } else if (!this.hasSupportedChannel() && this.hasUnSupportedChannel()) {
          this.segmentIoService.track(SegmentEvent.SignupOnlyUnsupportedChannelsSelected, {});

          this.stage = 3;
        } else {
          // No unsupported channels selected, on with the flow
          this.wizard.next();
        }
      },
      () => {
        this.saving = false;
      }
    );
  }

  toggleChannel(name) {
    this.channels[name] = !this.channels[name];
    this.isAtLeastOneEnabled();
  }

  isAtLeastOneEnabled() {
    // Do we have at least 1 channel enabled?
    const values = Object.values(this.channels);
    this.atLeastOneChannelEnabled = values.some((x) => x === true);
  }

  getChannelArray() {
    const channelsArr = [];
    for (const [key, value] of Object.entries(this.channels)) {
      if (value) {
        channelsArr.push(key);
      }
    }

    return channelsArr;
  }

  hasSupportedChannel(): boolean {
    return this.getChannelArray().some((channel) => SupportedChannels.indexOf(channel) >= 0);
  }

  hasUnSupportedChannel(): boolean {
    return this.getChannelArray().some((channel) => UnsupportedChannels.indexOf(channel) >= 0);
  }
}
